import { DocumentTagDTO, clone, randomColor } from "@pentacode/core";
import "@pentacode/ui/components";
import { LitElement, css, html } from "lit";
import { customElement, state } from "lit/decorators.js";
import { ColorInput } from "../../components/color-picker/color-input";
import { alert } from "../../elements/alert-dialog";
import { Checkbox } from "../../elements/checkbox";
import "../../elements/scroller";
import "../../elements/sortable-list";
import { app } from "../../init";
import { Routing } from "../../mixins/routing";
import { StateMixin } from "../../mixins/state";
import { shared } from "../../styles";

@customElement("ptc-settings-documents")
export class SettingsDocuments extends Routing(StateMixin(LitElement)) {
    routePattern = /settings\/documents/;

    get routeTitle() {
        return "Dokumente | Einstellungen";
    }

    get helpPage() {
        return "handbuch/einstellungen/dokumente/";
    }

    @state()
    private _loading = false;

    @state()
    private _existingDocumentTags: DocumentTagDTO[] = [];

    @state()
    private _documentTags: DocumentTagDTO[] = [];

    private get _hasChanges() {
        const hasTagChanged = (tag: DocumentTagDTO, existing?: DocumentTagDTO) => {
            return (
                !existing ||
                existing.name !== tag.name ||
                existing.color !== tag.color ||
                existing.hideFromEmployees !== tag.hideFromEmployees
            );
        };

        return (
            this._documentTags.length !== this._existingDocumentTags.length ||
            this._documentTags.some((tag) => {
                return hasTagChanged(
                    tag,
                    this._existingDocumentTags.find((t) => t.id === tag.id)
                );
            })
        );
    }

    handleRoute() {
        const fetchTags = async () => {
            try {
                const tags = await app.api.getDocumentTagsForCompany();

                // to detect changes correctly, we need to make sure that available tags and document tags don't share references
                this._documentTags = clone(tags);
                this._existingDocumentTags = tags;
            } catch (e) {
                void alert(e.message, { type: "warning" });
            }
        };

        void fetchTags();
    }

    private _reset() {
        // to detect changes correctly, we need to make sure that available tags and document tags don't share references
        this._documentTags = clone(this._existingDocumentTags);
    }

    private async _submit(e: Event) {
        e.preventDefault();

        this._loading = true;

        try {
            this._documentTags.forEach((t, i) => (t.order = i));
            await app.api.updateDocumentTagsForCompany(this._documentTags);

            // to detect changes correctly, we need to make sure that available tags and document tags don't share references
            this._existingDocumentTags = clone(this._documentTags);
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }

        this._loading = false;
    }

    private _removeDocumentTag(index: number) {
        this._documentTags.splice(index, 1);
        this.requestUpdate();
    }

    private async _newDocumentTag() {
        this._documentTags.push(
            new DocumentTagDTO({
                name: "",
                color: randomColor(this._existingDocumentTags.map((t) => t.color)),
                order: this._documentTags.length,
                hideFromEmployees: false,
            })
        );
        this.requestUpdate();
        await this.updateComplete;
        const tagWrappers = this.renderRoot.querySelectorAll(".documents .tag-item");
        const nameInput = tagWrappers[tagWrappers.length - 1]?.querySelector("[name=name]") as HTMLInputElement;
        nameInput?.focus();
    }

    static styles = [
        shared,
        ColorInput.styles,
        Checkbox.styles,
        css`
            .tag-item > input {
                padding: 0.7em 2em 0.7em 3em;
                width: 100%;
            }

            .tag-item > input:focus-visible {
                box-shadow: inset var(--color-primary) 0 0 0 1px;
            }

            .tag-item > ptc-color-input {
                position: absolute;
                left: 0.5em;
                top: 0.5em;
            }

            .tag-item > .buttons {
                position: absolute;
                right: 0.5em;
                top: 0.4em;
            }
        `,
    ];

    render() {
        if (!app.company) {
            return;
        }
        return html`
            <ptc-scroller class="fullbleed">
                <div class="double-padded narrow-content">
                    <form @submit=${this._submit} @input=${() => this.requestUpdate()} autocomplete="off">
                        <h2>Kategorien</h2>
                        <div class="box documents">
                            <ptc-sortable-list
                                .items=${this._documentTags}
                                .renderItem=${(tag: DocumentTagDTO, i: number) => html`
                                    <div class="tag-item border-bottom relative">
                                        <ptc-color-input
                                            name="color"
                                            .value=${tag.color}
                                            required
                                            @change=${(e: Event) => {
                                                tag.color = (e.target as HTMLInputElement).value;
                                                this.requestUpdate();
                                            }}
                                            class="right-margined"
                                        ></ptc-color-input>
                                        <input
                                            name="name"
                                            .value=${tag.name}
                                            required
                                            @input=${(e: Event) =>
                                                (tag.name = (e.target as HTMLInputElement).value.trim())}
                                            placeholder="Kategorie eingeben..."
                                            class="plain"
                                        />
                                        <div class="buttons">
                                            <button
                                                class="skinny transparent"
                                                type="button"
                                                id="visibilityToggle${i}"
                                                @click=${() => {
                                                    tag.hideFromEmployees = !tag.hideFromEmployees;
                                                    this.requestUpdate();
                                                }}
                                            >
                                                ${tag.hideFromEmployees
                                                    ? html`<ptc-ui-icon
                                                          name="icon.toggle.visibility-hidden"
                                                      ></ptc-ui-icon>`
                                                    : html`<ptc-ui-icon name="icon.toggle.visibility"></ptc-ui-icon>`}
                                            </button>
                                            <ptc-ui-tooltip for="visibilityToggle${i}">
                                                Dokumente mit dieser Kategorie können
                                                ${tag.hideFromEmployees ? html`<strong>nicht</strong>` : ""} vom
                                                Mitarbeiter eingesehen werden.
                                            </ptc-ui-tooltip>
                                            <button
                                                class="skinny transparent"
                                                @click=${() => this._removeDocumentTag(i)}
                                                type="button"
                                            >
                                                <ptc-ui-icon name="icon.action.delete"></ptc-ui-icon>
                                            </button>
                                        </div>
                                    </div>
                                `}
                                @item-moved=${this._submit}
                            >
                            </ptc-sortable-list>

                            <div>
                                <button
                                    class="transparent fill-horizontally"
                                    type="button"
                                    @click=${this._newDocumentTag}
                                >
                                    <ptc-ui-icon name="icon.action.add"></ptc-ui-icon> Neue Kategorie
                                </button>
                            </div>
                        </div>

                        <div
                            class="padded top-margined horizontal spacing evenly stretching layout"
                            ?hidden=${!this._hasChanges}
                        >
                            <button class="primary">Speichern</button>
                            <button type="button" class="transparent" @click=${this._reset}>Abbrechen</button>
                        </div>
                    </form>
                </div>
            </ptc-scroller>

            <div class="fullbleed center-aligning center-justifying vertical layout scrim" ?hidden=${!this._loading}>
                <ptc-ui-spinner ?active=${this._loading}></ptc-ui-spinner>
            </div>
        `;
    }
}
